import { type GuideBookingLink } from '@fruitful/types';
import {
    differenceInMinutes,
    isFuture,
    isPast,
    isToday,
    isTomorrow,
    parseISO,
} from 'date-fns';
import type { Href } from 'expo-router';

import { isEmpty } from '../common/misc';
import {
    type GuidanceSession,
    type GuidanceSessionType,
} from './types/GuidanceSessions';

export const getGuideBookingLinkUrl = (
    type: GuidanceSessionType,
    bookingLinks?: GuideBookingLink[] | null,
): string | undefined => {
    if (isEmpty(bookingLinks)) return undefined;
    return (bookingLinks ?? []).find((bookingLink) => bookingLink.type === type)
        ?.bookingLink;
};

export const sessionStartTimeIsWithinNMinutes = (
    session: GuidanceSession,
    minutes: number,
): boolean => {
    const diff = differenceInMinutes(parseISO(session.startTime), new Date());
    return diff >= 0 && diff < minutes;
};

export const sessionIsInProgress = (session: GuidanceSession): boolean => {
    const isAfterStart = isPast(parseISO(session.startTime));
    const isBeforeEnd = isFuture(parseISO(session.endTime));
    return isAfterStart && isBeforeEnd;
};

export const getMinutesInProgress = (
    session: GuidanceSession | null,
): number => {
    if (session == null || isFuture(parseISO(session.startTime))) {
        return 0;
    }
    const now = new Date();
    const startTime = parseISO(session.startTime);
    return differenceInMinutes(now, startTime);
};

export const sessionHasEnded = (session: GuidanceSession): boolean => {
    return isPast(parseISO(session.endTime));
};

export const getFormattedStartTime = (startTime: string) => {
    const sessionDate = new Date(startTime);
    const day = isToday(sessionDate)
        ? 'Today'
        : isTomorrow(sessionDate)
          ? 'Tomorrow'
          : sessionDate.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
            });
    const time = sessionDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
    });

    return `${day}, ${time}`;
};

export const getValidSessionBookingDoneRoute = (route?: string): Href => {
    if (isEmpty(route) || !route?.startsWith('/')) {
        // if not valid, return a fallback
        return '/'; // most session booking happens from onboarding or home
    }

    return route as Href;
};
